import { useLayoutEffect, useMemo, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useAuthMode } from "../../hooks/useAuthMode";
import GuestBanner, { GuestBannerState } from "./GuestBanner";

/**
 * GuestShellBanner is the wiring glue between the auth-state hook and the
 * presentational GuestBanner. App.tsx mounts a single instance above the
 * existing app bar; the banner is non-dismissible by design and only renders
 * when the caller is currently a guest.
 *
 * Usage stays a one-line mount — `<GuestShellBanner />` — so the App.tsx diff
 * stays minimal.
 */
const dayMs = 24 * 60 * 60 * 1000;

const stateFor = (daysLeft: number): GuestBannerState => {
  if (daysLeft <= 0) return "expire";
  if (daysLeft <= 2) return "warn";
  return "fresh";
};

const fmtDaysLabel = (daysLeft: number): string => {
  if (daysLeft <= 0) return "Ends today";
  if (daysLeft === 1) return "1 day left";
  return `${daysLeft} days left`;
};

const GuestShellBanner: React.FC = () => {
  const { mode, guest } = useAuthMode();
  const { loginWithRedirect } = useAuth0();
  const ref = useRef<HTMLDivElement>(null);

  const view = useMemo(() => {
    if (mode !== "guest" || !guest) return null;
    const daysLeft = Math.max(
      0,
      Math.ceil((guest.expiresAtMs - Date.now()) / dayMs)
    );
    const usedMB = Math.max(0, Math.round(guest.usedBytes / (1024 * 1024)));
    const totalMB = Math.max(
      1,
      Math.round(guest.quotaBytes / (1024 * 1024))
    );
    return { state: stateFor(daysLeft), daysLabel: fmtDaysLabel(daysLeft), usedMB, totalMB };
  }, [mode, guest]);

  // Publish the banner's rendered height as --guest-banner-height so full-height
  // layout (e.g. the left sidebar's calc(99vh - …)) can subtract it. Cleared
  // whenever the banner isn't shown so non-guest layout is unaffected. Measured
  // (not hard-coded) so a wrapped multi-row banner is accounted for too.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const el = ref.current;
    if (!el) {
      root.style.removeProperty("--guest-banner-height");
      return;
    }
    const apply = () =>
      root.style.setProperty("--guest-banner-height", `${el.offsetHeight}px`);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty("--guest-banner-height");
    };
  }, [view]);

  if (!view) return null;

  const onSave = () => {
    loginWithRedirect({
      appState: { targetUrl: "/initial-setting" },
      authorizationParams: { screen_hint: "signup" },
    });
  };

  return (
    <div ref={ref}>
      <GuestBanner
        state={view.state}
        daysLabel={view.daysLabel}
        usedMB={view.usedMB}
        totalMB={view.totalMB}
        onSave={onSave}
      />
    </div>
  );
};

export default GuestShellBanner;
